<template>
  <ion-app>
    <ion-router-outlet />
    <ConsentHost />
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, alertController } from '@ionic/vue';
import { addIcons } from 'ionicons';
import {
  locate,
  add,
} from 'ionicons/icons';
import { mapGetters, mapActions } from 'vuex';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import OldVersionError from '@/views/error/OldVersionError.vue';
import { checkAppVersion } from '@/utils/checkAppVersion';
import {
  isEmailVerificationRequired,
  isKitchenRoute,
  isLoginRoute,
  isPublicTokenRoute,
} from '@/utils/authUtils';
import { isKitchenKioskActive, resolveKitchenKioskTarget } from '@/utils/kitchenKiosk';
import { handlePartnerCheckoutReturn, hasPendingPartnerCheckout, installPartnerStripeCheckoutResume } from '@/utils/partnerCheckoutReturn';
import { reconcileAbandonedPartnerCheckout } from '@/utils/reconcileAbandonedPartnerCheckout';
import ConsentHost from '@/components/consent/ConsentHost.vue';

addIcons({
  'ios-add': add.ios,
  'md-add': add.md,
  'ios-locate': locate.md,
  'md-locate': locate.md,
});

export default {
  components: {
    IonApp,
    IonRouterOutlet,
    OldVersionError,
    ConsentHost,
  },
  data() {
    return {
      envAppVersion: import.meta.env.VITE_APP_VERSION,
      currentAppVersion: '',
    };
  },
  methods: {
    ...mapActions([
      'initAxiosInstance',
      'completeAuthFlow',
      'clearSessionState',
    ]),
    ...mapActions('tutorial', ['initTutorial']),
    async presentAlert(message) {
      const alert = await alertController.create({
        header: 'Hinweis',
        message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void checkAppVersion();
      }
    },
    async handleAuthStateChange(user) {
      if (isKitchenKioskActive()) {
        if (user) {
          try {
            await firebase.auth().signOut();
            await this.clearSessionState();
          } catch (error) {
            console.error('kitchen kiosk partner sign-out', error);
          }
        }
        if (!isKitchenRoute(this.$route)) {
          this.$router.replace(resolveKitchenKioskTarget());
        }
        return;
      }

      if (!user) {
        // Read before awaiting: a logout started in the UI navigates itself once it is done,
        // and a second navigation from here would cancel that one mid-transition.
        const logoutStartedInApp = this.$store.state.logoutInProgress;
        await this.clearSessionState();
        if (logoutStartedInApp) {
          return;
        }
        if (this.$route.name === 'login.profile' || this.$route.name === 'login.change-password') {
          this.$router.replace({ name: 'login' });
          return;
        }
        if (
          !isLoginRoute(this.$route)
          && !isPublicTokenRoute(this.$route)
          && !isKitchenRoute(this.$route)
        ) {
          this.$router.push({ name: 'login' });
        }
        return;
      }

      if (isPublicTokenRoute(this.$route) || isKitchenRoute(this.$route)) {
        return;
      }

      if (
        isEmailVerificationRequired(user)
        && (this.$route.name === 'login.profile' || this.$store.state.registrationInProgress)
      ) {
        return;
      }

      if (isEmailVerificationRequired(user)) {
        if (this.$route.name === 'login.email-action') {
          return;
        }
        if (this.$route.name !== 'login.verify-email') {
          this.$router.push({ name: 'login.verify-email' });
        }
        return;
      }

      await this.initTutorial();

      const result = await this.completeAuthFlow({
        router: this.$router,
        store: this.$store,
      });

      if (result?.pendingAdminVerification) {
        this.presentAlert('Ihre Daten werden überprüft. Bitte haben Sie noch etwas Geduld. Sie erhalten eine E-Mail, sobald Ihre Daten bestätigt wurden.');
      } else if (result?.failed) {
        this.presentAlert('Ein Fehler ist aufgetaucht');
      } else if (result?.authenticated) {
        import('@/utils/liveSync').then(({ initLiveSync }) => {
          initLiveSync(this.$store);
        });
        this.$store.dispatch('pushNotification/initPushNotifications');
        this.$store.dispatch('pushNotification/updateMessageToken');
        await reconcileAbandonedPartnerCheckout(this.$router);
        await this.processPartnerCheckoutIfNeeded();
      }
    },
    async processPartnerCheckoutIfNeeded() {
      if (this.$store.state.loginStatusCode !== 409) return;
      if (!hasPendingPartnerCheckout(this.$router)) return;
      await handlePartnerCheckoutReturn(this.$router);
    },
  },
  computed: {
    ...mapGetters([
      'getFreemium',
      'getFreemiumDaysLeft',
      'getFreemiumActive',
      'getFreemiumPackage',
    ]),
    isOverlayOpen() {
      return this.$store.getters['ui/isOverlayOpen'];
    },
  },
  watch: {
    isOverlayOpen: {
      handler(hasOverlay) {
        document.body.classList.toggle('ollaa-overlay-active', hasOverlay);
      },
      immediate: true,
    },
    '$route.fullPath': {
      handler() {
        this.processPartnerCheckoutIfNeeded().catch((error) => {
          console.error('Partner checkout return error', error);
        });
      },
    },
  },
  async created() {
    await this.initAxiosInstance();
    try {
      firebase.auth().onAuthStateChanged((user) => {
        this.handleAuthStateChange(user).catch((error) => {
          console.error('Auth state change error', error);
        });
      });
    } catch (e) {
      console.error('Request app version', e);
    }
  },
  mounted() {
    document.getElementById('loading_startup').style.display = 'none';
    void checkAppVersion();
    installPartnerStripeCheckoutResume();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeUnmount() {
    document.body.classList.remove('ollaa-overlay-active');
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
};
</script>


<style lang="scss">
html, body, #app, ion-app {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  --ion-default-font: 'Lexend Deca', sans-serif !important;
  --ion-font-family: 'Lexend Deca', sans-serif !important;
  font-family: 'Lexend Deca', sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  color: #2c3e50;
}

.ion-page {
  z-index: unset;
}

@media (min-width: 768px) {
  ion-app {
    max-width: 600px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.1);
    background: white;
  }

  html {
    background-color: #f4f4f4;
  }
}
</style>
